import { fetchCartItems } from "../../ducks/cart.duck";
import { buildCartItemsByGroupKey } from './CartHelpers';
import { fetchExchangeRates } from "../../ducks/exchangeRates.duck";
import { transactionLineItems, getUserAchievements } from "../../util/api";
import { storableError } from "../../util/errors";
import { denormalisedResponseEntities } from "../../util/data";
import { generateAchievementReviews } from "../../util/reviews";
import moment from "moment";

export const FETCH_LINE_ITEMS_REQUEST = 'app/CartPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/CartPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/CartPage/FETCH_LINE_ITEMS_ERROR';

export const FETCH_AUTHOR_REVIEWS_REQUEST = 'app/CartPage/FETCH_AUTHOR_REVIEWS_REQUEST';
export const FETCH_AUTHOR_REVIEWS_SUCCESS = 'app/CartPage/FETCH_AUTHOR_REVIEWS_SUCCESS';
export const FETCH_AUTHOR_REVIEWS_ERROR = 'app/CartPage/FETCH_AUTHOR_REVIEWS_ERROR';

const initialState = {
    lineItems: {},
    fetchLineItemsInProgress: {},
    fetchLineItemsError: {},
    lineItemsFetchedAt: {},
    authorReviews: {},
    authorListings: {},
    fetchAuthorReviewsInProgress: {},
    fetchAuthorReviewsError: {},
    emailVerified: {},
};

const cartPageReducer = (state = initialState, action = {}) => {
    const { type, payload } = action;
    switch (type) {
        case FETCH_LINE_ITEMS_REQUEST:
            return { ...state, fetchLineItemsInProgress: { ...state.fetchLineItemsInProgress, [payload.authorId]: true }, fetchLineItemsError: { ...state.fetchLineItemsError, [payload.authorId]: null } };
        case FETCH_LINE_ITEMS_SUCCESS:
            return {
                ...state,
                fetchLineItemsInProgress: { ...state.fetchLineItemsInProgress, [payload.authorId]: false },
                lineItems: { ...state.lineItems, [payload.authorId]: payload.lineItems },
                lineItemsFetchedAt: { ...state.lineItemsFetchedAt, [payload.authorId]: Date.now() },
            };
        case FETCH_LINE_ITEMS_ERROR:
            return { ...state, fetchLineItemsInProgress: { ...state.fetchLineItemsInProgress, [payload.authorId]: false }, fetchLineItemsError: { ...state.fetchLineItemsError, [payload.authorId]: payload } };
        case FETCH_AUTHOR_REVIEWS_REQUEST:
            return {
                ...state,
                fetchAuthorReviewsInProgress: {
                    ...state.fetchAuthorReviewsInProgress,
                    [payload]: true
                },
                fetchAuthorReviewsError: {
                    ...state.fetchAuthorReviewsError,
                    [payload]: null
                }
            };
        case FETCH_AUTHOR_REVIEWS_SUCCESS:
            return {
                ...state,
                fetchAuthorReviewsInProgress: {
                    ...state.fetchAuthorReviewsInProgress,
                    [payload.authorId]: false
                },
                authorReviews: {
                    ...state.authorReviews,
                    [payload.authorId]: payload.reviews
                },
                authorListings: {
                    ...state.authorListings,
                    [payload.authorId]: payload.totalListings
                },
                emailVerified: {
                    ...state.emailVerified,
                    [payload.authorId]: payload.emailVerified
                }
            };
        case FETCH_AUTHOR_REVIEWS_ERROR:
            return {
                ...state,
                fetchAuthorReviewsInProgress: {
                    ...state.fetchAuthorReviewsInProgress,
                    [payload.authorId]: false
                },
                fetchAuthorReviewsError: {
                    ...state.fetchAuthorReviewsError,
                    [payload.authorId]: payload.error
                }
            };
        default:
            return { ...state }
    }
};

export default cartPageReducer;


export const fetchLineItemsRequest = (authorId) => ({ type: FETCH_LINE_ITEMS_REQUEST, payload: { authorId } });
export const fetchLineItemsSuccess = (authorId, lineItems) => ({
    type: FETCH_LINE_ITEMS_SUCCESS,
    payload: { authorId, lineItems },
});
export const fetchLineItemsError = (error, authorId) => ({
    type: FETCH_LINE_ITEMS_ERROR,
    error: true,
    payload: { error, authorId },
});

export const fetchAuthorReviewsRequest = (authorId) => ({ type: FETCH_AUTHOR_REVIEWS_REQUEST, payload: authorId });
export const fetchAuthorReviewsSuccess = (authorId, reviews, totalListings, emailVerified) => ({
    type: FETCH_AUTHOR_REVIEWS_SUCCESS,
    payload: { authorId, reviews, totalListings, emailVerified },
});
export const fetchAuthorReviewsError = (error, authorId) => ({
    type: FETCH_AUTHOR_REVIEWS_ERROR,
    error: true,
    payload: { error, authorId },
});

// A fetch the browser cancelled - the user navigated away, the tab went away, or the
// component unmounted mid-request - rejects as a bare `TypeError: Failed to fetch`
// with no HTTP status. That is not a failure anyone can act on, but it was still being
// logged at console.error level, which is what surfaced two "fetching-line-items-failed"
// errors during cart teardown on mobile. Genuine failures (any real status, or any
// other error type) must keep logging.
const isAbortedRequest = e =>
    e?.status == null && (e instanceof TypeError || e?.name === 'AbortError');

export const fetchTransactionLineItems = (authorId, fetchLineItemsData) => dispatch => {
    dispatch(fetchLineItemsRequest(authorId));

    transactionLineItems(fetchLineItemsData)
        .then(response => {
            const lineItems = response.data;
            dispatch(fetchLineItemsSuccess(authorId, lineItems));
        })
        .catch(e => {
            // 404 means a listing was deleted — treat as empty rather than an error
            if (e?.status === 404) {
                dispatch(fetchLineItemsSuccess(authorId, []));
                return;
            }
            // Still dispatched either way, so the UI can render an error state; only
            // the console noise is suppressed for cancelled requests.
            dispatch(fetchLineItemsError(storableError(e), authorId));
            if (!isAbortedRequest(e)) {
                console.error(e, 'fetching-line-items-failed', { authorId });
            }
        });
};

export const fetchAuthorReviews = (authorId) => (dispatch, getState, sdk) => {
    dispatch(fetchAuthorReviewsRequest(authorId));

    return sdk.reviews
        .query({
            subject_id: authorId,
            state: 'public',
            include: ['author', 'author.profileImage'],
            'fields.image': ['variants.square-small', 'variants.square-small2x'],
        })
        .then(response => {
            const reviews = denormalisedResponseEntities(response);

            // Generate Custom reviews based on user's achievements.
            return getUserAchievements(authorId).then(({ data }) => {
                const {
                    emailVerified,
                    stripeConnected,
                    publishedListings,
                    displayName,
                } = data;

                const achievementReviews = generateAchievementReviews(
                    emailVerified,
                    stripeConnected,
                    publishedListings,
                    displayName
                );

                dispatch(
                    fetchAuthorReviewsSuccess(
                        authorId,
                        [...reviews],
                        publishedListings,
                        emailVerified,
                    )
                );
            });
        })
        .catch(e => dispatch(fetchAuthorReviewsError(e, authorId)));
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
    let { items = {} } = getState().cart;

    // Fetch exchange rates, do not fetch if already exists.
    const { exchangeRates } = getState().exchangeRate;

    if (typeof window != 'undefined' && exchangeRates && exchangeRates.length == 0) {
        dispatch(fetchExchangeRates());
    }


    if (!items || (!Object.keys(items || {}).length)) {
        const { itemsByAuthor } = await dispatch(fetchCartItems());
        items = itemsByAuthor;
    }

     // Fetch line items for each cart - one per (store, date range) group, the same
     // model the page renders (see buildCartItemsByGroupKey in CartHelpers.js) - so
     // the per-cart subtotal is priced for exactly that cart's items and dates.
     // Skips items with deleted listings.
     const fetchLineItemsData = Object.entries(buildCartItemsByGroupKey(items)).map(([groupKey, authorItems]) => {
        const validItems = authorItems.filter(item => item.listing?.id);
        return validItems.map(item => {
            const {
                bookingDates,
                timeZone,
                quantity,
                package: selectedPackage,
                listingId,
                listing,
                usageMultipleValues,
                otherOptionalFees,
                selectedFeeTypes,
                pickupAtOwnerLocation,
                returnAtOwnerLocation,
            } = item;

            const startDate = moment(bookingDates.startDate)
                .tz(timeZone)
                .toDate();
            const endDate = moment(bookingDates.endDate)
                .tz(timeZone)
                .toDate();
            const orderData = {
                bookingStart: startDate,
                bookingEnd: endDate,
                seats: parseInt(quantity, 10),
                package: selectedPackage,
                usageMultipleValues,
                selectedFeeTypes: selectedFeeTypes || otherOptionalFees || [],
                pickupAtOwnerLocation,
                returnAtOwnerLocation,
            };
            return {
                authorId: item.authorId,
                groupKey,
                orderData,
                listingId: listingId || listing?.id?.uuid,
                isOwnListing: false,
            };
        });
    }).filter(data => data.length > 0);

    const LINE_ITEMS_CACHE_TTL_MS = 60_000;
    const { lineItemsFetchedAt = {} } = getState().CartPage;

    const lineItemsPromise = fetchLineItemsData
        .filter(data => {
            const groupKey = data[0]?.groupKey;
            const cachedAt = lineItemsFetchedAt[groupKey];
            const isFresh = cachedAt && (Date.now() - cachedAt) < LINE_ITEMS_CACHE_TTL_MS;
            return !isFresh;
        })
        .map(data =>
            dispatch(
                fetchTransactionLineItems(data[0].groupKey, {
                    fetchLineItemsData: data.map(({ groupKey: _omit, ...rest }) => rest),
                })
            )
        );

    return Promise.all(lineItemsPromise).then((res) => {
        return res;
    });
};